// src/lib/auth-api.ts
// 認証 API クライアント。 cookie 経由なので credentials: include を必ず付ける

import type {
  SignupRequest, SignupResponse,
  LoginRequest, LoginResponse,
  ForgotRequest, ForgotResponse,
  ResetRequest, ResetResponse,
  VerifyResponse,
  MeResponse, LogoutResponse,
  AdminUsersResponse, AdminUserActionRequest, AdminUserActionResponse,
  AdminEventsResponse, AdminEventsFilters,
} from "@shared/auth";

async function authFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = await res.text();
  let parsed: unknown = null;
  try { parsed = body ? JSON.parse(body) : null; } catch { /* 非 JSON */ }
  if (!res.ok) {
    const err = (parsed && typeof parsed === "object" && "error" in parsed)
      ? String((parsed as { error: unknown }).error)
      : `${res.status} ${res.statusText}`;
    throw new Error(err);
  }
  return parsed as T;
}

export const authApi = {
  me: (): Promise<MeResponse> =>
    authFetch<MeResponse>("/api/auth/me"),

  signup: (req: SignupRequest): Promise<SignupResponse> =>
    authFetch<SignupResponse>("/api/auth/signup", {
      method: "POST", body: JSON.stringify(req),
    }),

  verify: (token: string): Promise<VerifyResponse> =>
    authFetch<VerifyResponse>(`/api/auth/verify?token=${encodeURIComponent(token)}`),

  login: (req: LoginRequest): Promise<LoginResponse> =>
    authFetch<LoginResponse>("/api/auth/login", {
      method: "POST", body: JSON.stringify(req),
    }),

  logout: (): Promise<LogoutResponse> =>
    authFetch<LogoutResponse>("/api/auth/logout", { method: "POST" }),

  forgot: (req: ForgotRequest): Promise<ForgotResponse> =>
    authFetch<ForgotResponse>("/api/auth/forgot", {
      method: "POST", body: JSON.stringify(req),
    }),

  reset: (req: ResetRequest): Promise<ResetResponse> =>
    authFetch<ResetResponse>("/api/auth/reset", {
      method: "POST", body: JSON.stringify(req),
    }),

  adminUsers: (): Promise<AdminUsersResponse> =>
    authFetch<AdminUsersResponse>("/api/admin/users"),

  adminUserAction: (req: AdminUserActionRequest): Promise<AdminUserActionResponse> =>
    authFetch<AdminUserActionResponse>("/api/admin/users/action", {
      method: "POST", body: JSON.stringify(req),
    }),

  adminEvents: (f: AdminEventsFilters = {}): Promise<AdminEventsResponse> => {
    const qs = new URLSearchParams();
    if (f.event_type) qs.set("event_type", f.event_type);
    if (f.email)      qs.set("email", f.email);
    if (f.from)       qs.set("from", f.from);
    if (f.to)         qs.set("to", f.to);
    if (f.limit)      qs.set("limit", String(f.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return authFetch<AdminEventsResponse>(`/api/admin/events${suffix}`);
  },
};
